import { load, type Store } from '@tauri-apps/plugin-store'

// Tauri環境かどうかを検出
export function isTauri(): boolean {
  if (typeof window === 'undefined') return false
  return '__TAURI_INTERNALS__' in window
}

// Tauri Store インスタンスのキャッシュ
let storeInstance: Store | null = null
let storeInitPromise: Promise<Store> | null = null

// グローバルメモリキャッシュ（同期アクセス用）
const globalMemoryCache: Record<string, string> = {}
let cacheInitialized = false
let cacheInitPromise: Promise<void> | null = null

// 保留中の非同期書き込みPromiseを追跡
let pendingSavePromises: Promise<void>[] = []

// 保留中の書き込みを追跡するヘルパー
function trackPendingSave(promise: Promise<void>): void {
  pendingSavePromises.push(promise)
  // 完了したらリストから除去
  promise.finally(() => {
    pendingSavePromises = pendingSavePromises.filter(p => p !== promise)
  })
}

// すべての保留中のTauri Store書き込みが完了するまで待機する
// アプリ終了前に呼び出すことで、セッションデータの確実なディスク保存を保証
export async function flushTauriStore(): Promise<void> {
  if (pendingSavePromises.length === 0) return
  await Promise.allSettled(pendingSavePromises)
  // allSettled後にさらに追加された可能性があるので再チェック
  if (pendingSavePromises.length > 0) {
    await Promise.allSettled(pendingSavePromises)
  }
}

// Tauri Store の初期化（一度だけ）
async function getStore(): Promise<Store> {
  if (storeInstance) return storeInstance

  if (storeInitPromise) return storeInitPromise

  storeInitPromise = load('auth.json', { autoSave: true } as any).then(store => {
    storeInstance = store
    return store
  })

  return storeInitPromise
}

// キャッシュ初期化の完了を待つPromise（外部から待機可能）
let cacheReadyResolve: (() => void) | null = null
const cacheReadyPromise = new Promise<void>((resolve) => {
  cacheReadyResolve = resolve
})

// キャッシュ初期化完了を通知するヘルパー
function notifyCacheReady() {
  if (cacheReadyResolve) {
    cacheReadyResolve()
    cacheReadyResolve = null // 一度だけ呼ばれるようにする
  }
}

// メモリキャッシュの初期化（一度だけ）
async function initializeCache(): Promise<void> {
  if (cacheInitialized) {
    notifyCacheReady()
    return
  }
  if (cacheInitPromise) return cacheInitPromise

  cacheInitPromise = (async () => {
    try {
      const store = await getStore()
      const keys = await store.keys()

      for (const key of keys) {
        const value = await store.get<string>(key)
        if (value !== null && value !== undefined) {
          globalMemoryCache[key] = value
        }
      }
      cacheInitialized = true
    } catch (error) {
      console.error('[TauriStorage] Cache init error:', error)
      cacheInitialized = true // エラーでも初期化完了とみなす
    }
    // 初期化完了を通知
    notifyCacheReady()
  })()

  return cacheInitPromise
}

// キャッシュの初期化が完了しているかどうか
export function isCacheReady(): boolean {
  return cacheInitialized
}

// キャッシュの初期化完了を待つ
export async function waitForCacheReady(): Promise<void> {
  if (cacheInitialized) return
  await cacheReadyPromise
}

// Supabase用のカスタムストレージアダプター
export function createTauriStorage() {
  // 初期化を開始（非同期）- initializeCache内でnotifyCacheReadyが呼ばれる
  initializeCache()

  return {
    getItem: (key: string): string | null => {
      return globalMemoryCache[key] ?? null
    },

    setItem: (key: string, value: string): void => {
      globalMemoryCache[key] = value

      // 非同期でTauri Storeに保存（Promiseを追跡）
      const savePromise = getStore().then(async (store) => {
        await store.set(key, value)
        await store.save()
      }).catch(error => {
        console.error('[TauriStorage] Save error:', error)
      })
      trackPendingSave(savePromise as Promise<void>)
    },

    removeItem: (key: string): void => {
      delete globalMemoryCache[key]

      // 非同期でTauri Storeから削除（Promiseを追跡）
      const deletePromise = getStore().then(async (store) => {
        await store.delete(key)
        await store.save()
      }).catch(error => {
        console.error('[TauriStorage] Delete error:', error)
      })
      trackPendingSave(deletePromise as Promise<void>)
    },
  }
}

// アプリ起動時にセッションを復元するためのヘルパー
// この関数を呼ぶと、メモリキャッシュも更新される
export async function loadSessionFromTauriStore(): Promise<string | null> {
  if (!isTauri()) return null

  try {
    // キャッシュの初期化を待つ
    await initializeCache()

    return globalMemoryCache['sb-auth-token'] ?? null
  } catch (error) {
    console.error('[TauriStorage] Load session error:', error)
    return null
  }
}

// ログアウト時にセッションを明示的に削除（同期的に待機）
export async function clearSessionFromTauriStore(): Promise<void> {
  if (!isTauri()) return

  try {
    const store = await getStore()

    // メモリキャッシュをクリア
    delete globalMemoryCache['sb-auth-token']

    // Tauri Storeから削除して保存を待つ
    await store.delete('sb-auth-token')
    await store.save()
  } catch (error) {
    console.error('[TauriStorage] Clear session error:', error)
  }
}
